const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

// Vega-Lite expresses inline rows either directly as `data.values` or as a
// top-level `datasets` map referenced by `data: { name: "table" }`. Both forms
// mean the spec brings its own data.
const carriesData = (
  candidate: unknown,
  datasets: Record<string, unknown> | undefined,
): boolean => {
  if (!isRecord(candidate)) return false;
  if (Array.isArray(candidate.values)) return true;
  return (
    typeof candidate.name === 'string' &&
    datasets !== undefined &&
    Object.prototype.hasOwnProperty.call(datasets, candidate.name)
  );
};

export const specHasInlineData = (spec: Record<string, unknown>): boolean => {
  const datasets = isRecord(spec.datasets) ? spec.datasets : undefined;
  return Array.isArray(spec.data)
    ? spec.data.some(entry => carriesData(entry, datasets))
    : carriesData(spec.data, datasets);
};

// A block tool's rows are the raw SQL result, which the chart spec is free to
// aggregate differently (e.g. counts per day). Overwriting a spec that already
// embeds its own data points the encodings at fields that do not exist, which
// renders an empty chart with a NaN axis — so only fill in data when the spec
// has none. Returns the spec unchanged when it is unparseable or there are no
// rows to add.
export const injectRowsIntoSpec = (
  spec: string,
  rows: Record<string, unknown>[] | undefined | null,
): string => {
  if (!rows?.length) return spec;

  let parsed: unknown;
  try {
    parsed = JSON.parse(spec);
  } catch {
    return spec;
  }
  if (!isRecord(parsed)) return spec;

  if (specHasInlineData(parsed)) return spec;

  parsed.data = { values: rows };
  return JSON.stringify(parsed);
};
